import React, { useEffect, useState } from 'react';
import withRouter from '../../../components/withRouter';
import TableContainer from '../../../components/TableContainerReactTable';
import { getSites } from '../services/siteApi';
import { Site, IabCategory } from '../types/site';
import { useProfile } from '../../(identity)/hooks/UserHooks';
import AddEditSiteModal from '../components/site/AddEditSiteModal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, ExternalLink, Pencil, Plus } from 'lucide-react';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

const Index = (props: any) => {
    const { userProfile } = useProfile();
    const [data, setData] = useState<Site[]>([]);
    const [dataToEdit, setDataToEdit] = useState<Site | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [isAddEditModalOpen, setIsAddEditModalOpen] = useState<boolean>(false);

    useEffect(() => {
        getSites().then((res) => {
            setData(res);
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    const handleUpdateData = (newData: Site) => {
        const updatedData = data.map((item) => {
            if (item.id === newData.id) {
                return newData;
            }
            return item;
        });
        setData(updatedData);
    };

    const handleAddData = (newData: Site) => {
        setData([...data, newData]);
    };

    const toggleAddEditModal = () => {
        setIsAddEditModalOpen(!isAddEditModalOpen);
    };

    const dateFormatOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        timeZoneName: 'shortOffset',
    };

    const columns = [
        {
            header: '#',
            cell: (cell: any) => {
                return (
                    <span className="fw-semibold">{cell.getValue()}</span>
                );
            },
            accessorKey: 'id',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Media Cad',
            accessorKey: 'mediaCad',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Site',
            accessorKey: 'site',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'App',
            accessorKey: 'app',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Categoria',
            cell: (cell: any) => {
                const categories: IabCategory[] = cell.row.original.iabCategories || [];
                if (categories.length === 0) {
                    return <span className="text-muted-foreground">—</span>;
                }
                const mainCategory = categories[0];
                const subCategories = categories.slice(1);
                const cellId = `cat_${cell.row.original.id}`;
                return (
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-info-subtle text-info cursor-pointer">
                                    {mainCategory.tier1 || mainCategory.name}
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p className="whitespace-pre-line">{categories.map((c) => c.tier1 ? [c.tier1, c.tier2, c.tier3, c.tier4].filter(Boolean).join(' > ') : c.name).join('\n')}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                );
            },
            enableColumnFilter: false,
            enableSorting: false,
        },
        {
            header: 'TAGs',
            accessorKey: 'tags.length',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Placements',
            accessorKey: 'placements.length',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Pedidos',
            accessorKey: 'orders.length',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Criado em',
            accessorFn: (row: any) => {
                const date = new Date(row.createdAt);
                return date.toLocaleDateString('pt-BR', dateFormatOptions);
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Atualizado em',
            accessorFn: (row: any) => {
                const date = new Date(row.updatedAt);
                return date.toLocaleDateString('pt-BR', dateFormatOptions);
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Actions',
            cell: (cell: any) => {
                const row = cell.row.original;
                let actionCell = [];

                if (userProfile && userProfile.role === 'ROLE_SUPER_ADMIN') {
                    actionCell.push(<TooltipProvider key="action_edit">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 text-primary hover:bg-primary/10 h-9 px-4 py-2"
                                    onClick={() => {
                                        setDataToEdit(row);
                                        toggleAddEditModal();
                                    }}
                                >
                                    <Pencil className="h-4 w-4" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Editar</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>);
                }
                return <div className="flex gap-2">{actionCell}</div>;
            },
            enableColumnFilter: false,
        },
    ];

    document.title = 'Ad Network | Ad Pulse';
    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12">
                            <Card>
                                <CardHeader>
                                    <div className="flex items-center justify-between">
                                        <CardTitle>Sites</CardTitle>
                                        <Button
                                            onClick={() => {
                                                setDataToEdit(null);
                                                toggleAddEditModal();
                                            }}
                                            className="flex items-center gap-2"
                                        >
                                            <Plus className="h-4 w-4" />
                                            Adicionar site
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    {!loading ? (
                                        <TableContainer
                                            columns={(columns || [])}
                                            data={(data || [])}
                                            isGlobalFilter={true}
                                            customPageSize={30}
                                            SearchPlaceholder="Procurar..."
                                        />
                                    ) : (
                                        <div className="text-center">
                                            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
                                        </div>
                                    )}

                                    <AddEditSiteModal
                                        site={dataToEdit}
                                        isOpen={isAddEditModalOpen}
                                        toggle={toggleAddEditModal}
                                        onAddData={handleAddData}
                                        onUpdateData={handleUpdateData}
                                    />
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default withRouter(Index);
